import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_CRIMSON } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/my-heart.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/my-heart.mp3',
  attribution:
    '"My Heart" by Different Heaven & EH!DE [NCS Release] - https://ncs.io/MyHeart',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('my-heart', variant),
    slug: buildVariantSlug('my-heart', variant),
    title: buildVariantTitle('My Heart', variant),
    description:
      'Fast electro at 174 BPM with heavy drops. The chorus sustains an 8th-note assault - not for newcomers.',
    bpm: onsets.bpm,
    difficulty: 4,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_CRIMSON,
    audio,
  });

  // 60s @ 174 BPM ≈ 174 beats. Build → drop → bridge → second drop.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 64, kind: 'drop' });
  builder.section({ startBeat: 64, endBeat: 96, kind: 'bridge' });
  builder.section({ startBeat: 96, endBeat: 174, kind: 'drop' });

  // Build: kicks only, every other one. Fast tempo means even halved is brisk.
  const buildPads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['kick'],
    everyNth: 2,
    minSpacing: 0.75,
  });

  // First drop: kick + strong snare. Tighter spacing for the high-energy
  // section but still gives the player time at 174 BPM (1 beat ≈ 345ms).
  // Global 0.5-beat floor across kick+snare so a kick on N.0 plus a snare on
  // N.25 doesn't merge to a 86ms double-tap (physically unplayable).
  const drop1 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['kick'],
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['snare'],
      minIntensity: 0.45,
      minSpacing: 0.75,
    }),
  );

  // Bridge: lead melody only - melodic relief between drops.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 96,
    kinds: ['lead'],
    minIntensity: 0.35,
    minSpacing: 1,
  });

  // Second drop: kick + snare + ride the strongest leads. Climax density.
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 96,
      untilBeat: 172,
      kinds: ['kick', 'snare'],
      minIntensity: 0.3,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 96,
      untilBeat: 172,
      kinds: ['lead'],
      minIntensity: 0.55,
      minSpacing: 1,
    }),
  );

  const padBeats = mergeBeats(buildPads, drop1, bridgePads, drop2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // 174 BPM means a beat is only ~345ms; require longer gaps before
  // inserting a chamber so we don't stuff one in between every cluster.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 172,
    minGapBeats: 8,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(174);
  return builder.build();
}

export const MY_HEART_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const MY_HEART_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(MY_HEART_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
